import safeWindow from './safeWindows';

/**
 * Normalizes a URL for prefix comparison.
 * - Ensures the path ends with a slash.
 * - Removes query strings and fragments.
 */
export function normalizeUrl(url: string): string {
  try {
    const parsedUrl = new URL(url, safeWindow.location.origin);
    const path = parsedUrl.pathname.endsWith('/') ? parsedUrl.pathname : `${parsedUrl.pathname}/`;
    return `${parsedUrl.protocol}//${parsedUrl.host}${path}`;
  } catch {
    const trimmed = url.split(/[?#]/)[0];
    return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
  }
}

/**
 * Checks if the current URL is allowed based on allowed and excluded URL lists.
 *
 * @param allowedUrls - Array of allowed URL prefixes. Empty array means allow all.
 * @param excludedUrls - Array of excluded URL prefixes. Empty array means exclude none.
 * @returns true if the current URL is allowed, false otherwise.
 *
 * Logic:
 * 1. If URL matches any excludedUrls prefix → return false
 * 2. If allowedUrls is empty → return true (allow all)
 * 3. If URL matches any allowedUrls prefix → return true
 * 4. Otherwise → return false
 */
export function isUrlAllowed(allowedUrls: string[] = [], excludedUrls: string[] = []): boolean {
  const currentUrl = normalizeUrl(safeWindow.location.href);
  const normalizedAllowed = allowedUrls.map(normalizeUrl);
  const normalizedExcluded = excludedUrls.map(normalizeUrl);

  // Check if URL is excluded
  const isExcluded = normalizedExcluded.some((prefix) => currentUrl.startsWith(prefix));
  if (isExcluded) {
    return false;
  }

  // If allowedUrls is empty, allow all (unless excluded)
  if (normalizedAllowed.length === 0) {
    return true;
  }

  // Check if URL matches any allowed prefix
  const isAllowed = normalizedAllowed.some((prefix) => currentUrl.startsWith(prefix));
  return isAllowed;
}
